import { Link } from 'react-router-dom'
import { Loader2, MessageSquare } from 'lucide-react'
import type { EventLifecycle } from '@lib/enums/event-lifecycle.enum'
import type { CoupleFeedbackDto } from '@lib/dto/couple-feedback.dto'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useEventCoupleFeedback } from '@/hooks/useCoupleFeedback'
import { SENTIMENT_TITLE, STATUS_LABEL, STATUS_TONE } from '@/lib/couple-feedback-labels'
import { Badge } from '@/components/ui/badge'
import type { PageSection } from '@/components/ui/section-nav'
import { ErrorState } from '@/components/ui/states'

/**
 * What the couple has said about this event, on the staff event page.
 * Each row opens the answer. An empty finished booking says so.
 */
function EventCoupleFeedback({
  pending,
  error,
  rows,
  onRetry,
}: {
  pending: boolean
  error: string | null
  rows: CoupleFeedbackDto[] | null
  onRetry: () => void
}) {
  if (pending) {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading feedback…
      </p>
    )
  }
  if (error !== null) return <ErrorState message={error} onRetry={onRetry} />
  if (rows === null || rows.length === 0) return <p className="text-sm text-ink-muted">Nothing from the couple yet.</p>
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
      {rows.map((row) => (
        <li key={row.id} className="min-w-0">
          <Link
            to={`/feedback/${row.id}`}
            className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1 wrap-anywhere px-4 py-3 hover:bg-surface-hover"
          >
            <span className="min-w-0 text-sm font-medium text-ink">{row.authorName}</span>
            <AnswerBadge row={row} />
            <span className="basis-full text-small text-ink-muted">{SENTIMENT_TITLE[row.sentiment]}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function AnswerBadge({ row }: { row: CoupleFeedbackDto }) {
  if (row.sentiment === 'wonderful') return <Badge tone="neutral">Kind words</Badge>
  return <Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
}

/**
 * The Feedback section for staff who can edit the event. Hidden until the day
 * is over, or until an answer (or a failed load) is already there. The page
 * spreads the result into its section list.
 */
export function useEventFeedbackSection(
  eventId: string,
  event: { lifecycle: EventLifecycle; hasFinished: boolean } | null,
  enabled: boolean,
): PageSection | null {
  const query = useEventCoupleFeedback(eventId, enabled)
  if (!enabled || event === null) return null
  const hasAnswers = (query.data?.length ?? 0) > 0
  const show = (event.lifecycle === 'booked' && event.hasFinished) || hasAnswers || query.isError
  if (!show) return null
  return {
    id: 'feedback',
    label: 'Feedback',
    icon: MessageSquare,
    description: 'What each person in the couple said about the day. Private to the venue, and separate from a public review.',
    content: (
      <EventCoupleFeedback
        pending={query.isPending}
        error={query.isError ? requestErrorMessage(query.error) : null}
        rows={query.data ?? null}
        onRetry={() => void query.refetch()}
      />
    ),
  }
}
